import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useBooleanFlagValue } from "@openfeature/react-sdk";
import { Flex } from "antd";

import { useIsDemoExperience } from "@av/apps/common-apps/platform/services/demo-experience/demo";
import type {
  CandidateDataUI,
  UploadFileExtended,
} from "@av/apps/public/jobs/services/apply/types";
import { usePublicFetchSingleJobPost } from "@av/apps/public/jobs/services/public-job-posts/public-job-posts";
import type { ApplicationDocumentType } from "@av/apps/recruitment/ats-common/types";
import { Card } from "@av/components/atoms/Card";
import { Form } from "@av/components/atoms/form/Form";
import { DocumentUploadField } from "./DocumentUploadField";
import { DocumentUploadModal } from "./DocumentUploadModal";
import { SupportingDocumentsUploadField } from "./SupportingDocumentsUploadField";
import { SupportingDocumentsUploadModal } from "./SupportingDocumentsUploadModal";

import type { UploadFile } from "antd/es/upload/interface";
import type { NamePath } from "rc-field-form/lib/interface";

export function DocsUploadCard() {
  const { t } = useTranslation();

  const form = Form.useFormInstance<CandidateDataUI>();
  const [currentDocumentType, setCurrentDocumentType] = useState<ApplicationDocumentType>("RESUME");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [supportingDocumentsModalOpen, setSupportingDocumentsModalOpen] = useState(false);
  const [supportingDocumentReplacement, setSupportingDocumentReplacement] = useState<{
    fileUid: string;
    replacementFile: UploadFile;
  }>();
  const [selectedDocument, setSelectedDocument] = useState<UploadFileExtended | undefined>(
    undefined
  );

  const isDemo = useIsDemoExperience();
  const isCoverLetterEnabled = useBooleanFlagValue("ats_multi_docs", false);
  const areCandidateSupportingDocumentsEnabled = useBooleanFlagValue(
    "ats_candidate_supporting_document_uploads",
    false
  );
  const { companyId, jobPostId } = useParams();
  const { data: jobPost } = usePublicFetchSingleJobPost(companyId, jobPostId);

  const RESUME_NAME: NamePath = "documents.resume";
  const COVER_LETTER_NAME: NamePath = "documents.coverLetter";
  const SUPPORTING_DOCUMENTS_NAME: NamePath = "documents.supportingDocuments";
  const RESUME_PATH = RESUME_NAME.split(".");
  const COVER_LETTER_PATH = COVER_LETTER_NAME.split(".");
  const SUPPORTING_DOCUMENTS_PATH = SUPPORTING_DOCUMENTS_NAME.split(".");
  const resume = Form.useWatch(RESUME_NAME, { form });
  const coverLetter = Form.useWatch(COVER_LETTER_NAME, { form });

  const openUploadModal = (target: ApplicationDocumentType) => {
    setCurrentDocumentType(target);
    setSelectedDocument(target === "RESUME" ? resume : coverLetter);
    setUploadModalOpen(true);
  };

  const closeUploadModal = () => {
    setSelectedDocument(undefined);

    setUploadModalOpen(false);
  };

  const closeSupportingDocumentsModal = () => {
    setSupportingDocumentsModalOpen(false);
    setSupportingDocumentReplacement(undefined);
  };

  const handleDelete = (documentType: ApplicationDocumentType) => {
    if (documentType === "RESUME") {
      form.setFieldValue(RESUME_PATH, undefined);
    } else {
      form.setFieldValue(COVER_LETTER_PATH, undefined);
    }
  };

  return (
    <Card
      title={t("recruitment.jobApplication.section_docs")}
      testIdPrefix="candidate-application-docs"
    >
      <Flex vertical gap={12}>
        <Form.Item
          name={RESUME_NAME}
          testIdPrefix="candidate-application-resume"
          required={jobPost?.isResumeRequired === true}
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
          label={t("recruitment.jobApplication.label_resume")}
        >
          <DocumentUploadField
            file={resume}
            onDelete={() => handleDelete("RESUME")}
            onUploadClick={() => openUploadModal("RESUME")}
            testIdPrefix="candidate-application-resume"
          />
        </Form.Item>

        {(isDemo || isCoverLetterEnabled) && (
          <Form.Item
            name={COVER_LETTER_NAME}
            testIdPrefix="candidate-application-cover-letter"
            required={jobPost?.isCoverLetterRequired === true}
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
            label={t("recruitment.jobApplication.label_coverLetter")}
          >
            <DocumentUploadField
              file={coverLetter}
              onDelete={() => handleDelete("COVER_LETTER")}
              onUploadClick={() => openUploadModal("COVER_LETTER")}
              testIdPrefix="candidate-application-cover-letter"
            />
          </Form.Item>
        )}

        {(isDemo || areCandidateSupportingDocumentsEnabled) && (
          <Form.Item
            name={SUPPORTING_DOCUMENTS_NAME}
            testIdPrefix="candidate-application-supporting-documents"
            required={jobPost?.isSupportingDocumentsRequired === true}
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
            label={t("recruitment.jobApplication.label_supportingDocuments")}
          >
            <SupportingDocumentsUploadField
              onUploadClick={() => {
                setSupportingDocumentReplacement(undefined);
                setSupportingDocumentsModalOpen(true);
              }}
              onReplace={(file, replacementFile) => {
                setSupportingDocumentReplacement({ fileUid: file.uid, replacementFile });
                setSupportingDocumentsModalOpen(true);
              }}
            />
          </Form.Item>
        )}
      </Flex>

      <DocumentUploadModal
        open={uploadModalOpen}
        title={t("recruitment.jobApplication.documents_add")}
        fileList={selectedDocument ? [selectedDocument] : []}
        onCancel={closeUploadModal}
        onFilesSelected={([file]) => {
          setSelectedDocument(
            file
              ? ({ ...file, documentType: currentDocumentType } as UploadFileExtended)
              : undefined
          );
        }}
        onConfirm={() => {
          if (currentDocumentType === "RESUME") {
            form.setFieldValue(RESUME_PATH, selectedDocument);
          } else {
            form.setFieldValue(COVER_LETTER_PATH, selectedDocument);
          }

          closeUploadModal();
        }}
      />
      {supportingDocumentsModalOpen ? (
        <SupportingDocumentsUploadModal
          fileList={form.getFieldValue(SUPPORTING_DOCUMENTS_PATH) ?? []}
          initialReplacement={supportingDocumentReplacement}
          onCancel={closeSupportingDocumentsModal}
          onConfirm={(supportingDocuments) => {
            form.setFieldValue(SUPPORTING_DOCUMENTS_PATH, supportingDocuments);
            closeSupportingDocumentsModal();
          }}
        />
      ) : null}
    </Card>
  );
}
